import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import type { AspectRatio, TemplateBundle } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { VideoStateTool } from './VideoStateTool';

import {
    authoredHtmlSceneIds,
    htmlScenesRelayoutNote,
} from 'js/src/agent/html-design/htmlScenesRelayout';

import { getTemplateBundle } from 'js/src/data/templates';
import { checkpointVideo } from 'js/src/data/video-data';
import Flags from 'js/src/libs/flags';
import { saveRawLuminary } from 'js/src/luminary';

export const setAspectRatioSchema = z.object({
    aspect_ratio: z.enum(VideoData.ALL_ASPECT_RATIOS),
});
export type SetAspectRatioArgs = z.infer<typeof setAspectRatioSchema>;

export class SetAspectRatioTool extends VideoStateTool<typeof setAspectRatioSchema> {
    protected override readonly schema = setAspectRatioSchema;
    protected override readonly toolName = TOOL_NAMES.setAspectRatio;
    public override readonly subagentAccess = SubagentAccess.BLOCKED;
    protected override readonly toolDescription = 'Set the aspect ratio for the video';

    public override getToolDisplayName(): string {
        return 'Setting aspect ratio';
    }

    public get statusIcon(): string {
        return 'aspect_ratio';
    }

    public async execute(args: SetAspectRatioArgs, context: ToolExecutionContext) {
        const { aspect_ratio } = args;
        const { parentActionGroup } = context;

        const unsupportedError = this.describeUnsupportedSceneDesigns(aspect_ratio);
        if (unsupportedError) {
            return { result: unsupportedError, isSuccess: false };
        }

        try {
            const htmlSceneIds =
                Flags.canUseChartSubagent() && this.luminary.aspectRatio !== aspect_ratio
                    ? authoredHtmlSceneIds(this.luminary)
                    : [];
            const from = { ...this.luminary.referenceResolution };
            this.luminary.aspectRatio = aspect_ratio;

            // Save changes to enable undo/redo functionality
            // NOTE: we can not save luminary without any scenes as it's considered an invalid state (for the frontend)
            if (this.luminary.scenes.length > 0) {
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            }

            const relayout =
                htmlSceneIds.length > 0
                    ? `\n\n${htmlScenesRelayoutNote(this.luminary, htmlSceneIds, from, this.luminary.referenceResolution)}`
                    : '';
            return `Success: Set video aspect ratio to ${aspect_ratio}${relayout}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting aspect ratio: ${errorMessage}`;
        }
    }

    /**
     * A template may support fewer than all aspect ratios, and its advertised
     * `supported_aspect_ratios` only reflect its first scene design. Switching luminary to a
     * ratio that any scene design in the video lacks data for throws from deep inside the
     * resize cascade, after the video has already been half-migrated. Check the bundle up
     * front so the agent gets a clear, actionable failure and the video is left untouched.
     */
    private describeUnsupportedSceneDesigns(aspectRatio: AspectRatio): string | null {
        const bundle = getTemplateBundle(this.getState(), this.luminary.template.id);
        if (!bundle) {
            return null;
        }

        const usedSceneDesignIds = new Set(
            this.luminary.scenes
                // Brand scenes are built from a compiled-in partial, not the bundle
                .filter((scene) => scene.sceneCategory !== VideoData.CATEGORY_BRAND)
                .map((scene) => scene.sceneDesign),
        );
        const unsupportedSceneDesignIds = [...usedSceneDesignIds].filter(
            (sceneDesignId) => !sceneDesignHasAspectRatio(bundle, sceneDesignId, aspectRatio),
        );
        if (unsupportedSceneDesignIds.length === 0) {
            return null;
        }

        const ratiosSupportedByAllScenes = VideoData.ALL_ASPECT_RATIOS.filter((ratio) =>
            [...usedSceneDesignIds].every((sceneDesignId) =>
                sceneDesignHasAspectRatio(bundle, sceneDesignId, ratio),
            ),
        );
        const alternatives =
            ratiosSupportedByAllScenes.length > 0
                ? `Every scene in this video supports: ${ratiosSupportedByAllScenes.join(', ')}.`
                : 'No single aspect ratio is supported by every scene in this video.';

        return (
            `Cannot set aspect ratio to "${aspectRatio}": scene design(s) ` +
            `${unsupportedSceneDesignIds.map((id) => `"${id}"`).join(', ')} have no ` +
            `${aspectRatio} layout in template "${this.luminary.template.id}". ${alternatives} ` +
            'Pick one of those, or swap the affected scenes to a scene design that supports it first.'
        );
    }
}

function sceneDesignHasAspectRatio(
    bundle: TemplateBundle,
    sceneDesignId: string,
    aspectRatio: AspectRatio,
): boolean {
    return !!bundle.sceneDesigns[sceneDesignId]?.[aspectRatio];
}
